import {
  ApolloError,
  useMutation,
  useQuery,
  useReactiveVar
} from '@apollo/client'
import {
  Button,
  Chip,
  Divider,
  Paper,
  TextField,
  Typography
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import NotesOutlinedIcon from '@material-ui/icons/NotesOutlined'
import RefreshIcon from '@material-ui/icons/Refresh'
import momentJalaali from 'moment-jalaali'
import React, {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { buildFeatureSet, credentialsVar } from '../../../../cache/auth'
import {
  ADD_BOOKING_NOTE_MUTATION,
  BOOKING_NOTES_QUERY,
  BOOKING_NOTE_MAX_CHARACTERS,
  BookingNote,
  BookingNotesPage
} from '../../../../graphql/bookingNotes'
import { formatLoginAttemptTime } from '../../../../helpers/date'
import { getDirection, getTranslate, lang } from '../../../../localization'
import { isForbidden } from '../../access'
import useStyles from './styles'

// Cryptographic UUID, not a timestamp: keep this identity through a failed response,
// so retrying a request whose server write succeeded cannot append a duplicate PMS note.
export function newNoteId(): string {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 15) | 64
  bytes[8] = (bytes[8] & 63) | 128
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16
  )}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function noteStateLabel(state: string): string {
  const labels: Record<string, string> = {
    queued: 'Note queued',
    pending: 'Note pending',
    awaiting_mapping: 'Waiting for PMS booking mapping',
    processing: 'Note pending',
    partial: 'Note partially delivered',
    delivered: 'Note delivered',
    failed: 'Note delivery failed',
    blocked: 'Note delivery blocked',
    uncertain: 'Note delivery needs verification',
    attention: 'Note delivery needs attention',
    unknown: 'Delivery status unavailable'
  }
  return getTranslate(
    labels[state.toLowerCase()] || 'Note delivery needs attention'
  )
}

function forbidden(error: ApolloError): boolean {
  return (
    isForbidden(error) ||
    error.graphQLErrors.some(
      (item) =>
        item.extensions?.code === 'FORBIDDEN' || item.extensions?.status === 403
    )
  )
}

export function noteRejection(
  error: unknown
): 'permission' | 'conflict' | 'invalid' | null {
  if (!(error instanceof ApolloError)) return null
  const networkStatus = (error.networkError as { statusCode?: number } | null)
    ?.statusCode
  const statuses = [
    networkStatus,
    ...error.graphQLErrors.map((item) =>
      Number(
        item.extensions?.status ||
          item.extensions?.statusCode ||
          (item.extensions?.http as { status?: number } | undefined)?.status
      )
    )
  ]
  const codes = error.graphQLErrors.map((item) =>
    String(item.extensions?.code || '').toUpperCase()
  )
  if (
    statuses.some((status) => status === 401 || status === 403) ||
    codes.some((code) => ['FORBIDDEN', 'UNAUTHORIZED'].includes(code))
  )
    return 'permission'
  if (
    statuses.includes(409) ||
    codes.some((code) =>
      ['CONFLICT', 'NOTE_IDEMPOTENCY_CONFLICT'].includes(code)
    )
  )
    return 'conflict'
  if (
    statuses.includes(422) ||
    codes.some((code) =>
      ['BAD_USER_INPUT', 'UNPROCESSABLE_ENTITY'].includes(code)
    )
  )
    return 'invalid'
  return null
}

interface Props {
  reservationNumber: string
}

type NotesHistory = {
  reservationNumber: string
  capability?: BookingNotesPage
  notesByRoom: Map<string, BookingNote[]>
  loading: boolean
  error: string
  historyLoaded: boolean
  hidden: boolean
  mayAdd: boolean
  mappedRooms: string[]
  refresh: () => void
  append: (note: BookingNote) => void
}
const HistoryContext = createContext<NotesHistory | null>(null)

const EMPTY_NOTES: BookingNote[] = []

export function roomNoteState(
  note: BookingNote,
  deliveries: BookingNote['deliveries']
): string {
  if (!deliveries.length)
    return note.roomNumbers.length > 1 &&
      !['awaiting_mapping', 'blocked'].includes(note.state.toLowerCase())
      ? 'unknown'
      : note.state
  const states = Array.from(
    new Set(deliveries.map((delivery) => delivery.state.toLowerCase()))
  )
  if (states.length === 1) return states[0]
  if (states.includes('delivered')) return 'partial'
  if (states.includes('uncertain')) return 'uncertain'
  if (states.includes('failed')) return 'failed'
  if (states.includes('blocked')) return 'blocked'
  return 'pending'
}

// Build roomstay projections once per history change, not once per room render.
// Reuse projections and room arrays so adding to one room does not invalidate
// every other room's memoized history. Explicit pagination retains all loaded
// notes; silently evicting older notes would make the displayed history lie.
export function indexRoomNotes(
  notes: BookingNote[],
  previous: Map<string, BookingNote[]>,
  projections: WeakMap<BookingNote, Map<string, BookingNote>>
): Map<string, BookingNote[]> {
  const index = new Map<string, BookingNote[]>()
  notes.forEach((note) => {
    let scoped = projections.get(note)
    if (!scoped) {
      scoped = new Map()
      const deliveriesByRoom = new Map<string, BookingNote['deliveries']>()
      note.deliveries.forEach((delivery) => {
        const deliveries = deliveriesByRoom.get(delivery.roomNumber) || []
        deliveries.push(delivery)
        deliveriesByRoom.set(delivery.roomNumber, deliveries)
      })
      note.roomNumbers.forEach((room) => {
        const deliveries = deliveriesByRoom.get(room) || []
        scoped?.set(room, {
          ...note,
          roomNumbers: [room],
          deliveries,
          state: roomNoteState(note, deliveries)
        })
      })
      projections.set(note, scoped)
    }
    scoped.forEach((projection, room) => {
      const roomNotes = index.get(room) || []
      roomNotes.push(projection)
      index.set(room, roomNotes)
    })
  })
  index.forEach((roomNotes, room) => {
    const prior = previous.get(room)
    if (
      prior?.length === roomNotes.length &&
      roomNotes.every((note, position) => note === prior[position])
    )
      index.set(room, prior)
  })
  return index
}

// PMS registration is a local wall-clock value, not an instant. Never pass it
// through a browser timezone conversion: that would shift the hotel's clock.
export function formatPmsRegistration(value?: string | null): string {
  if (!value || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value))
    return getTranslate('Unavailable')
  const [year, month, day, hour, minute] = value.split(/[-T:]/).map(Number)
  const date = new Date(0)
  // setUTCFullYear avoids Date.UTC's special 1900 offset for years 00–99.
  date.setUTCFullYear(year, month - 1, day)
  if (
    year < 1 ||
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day ||
    hour > 23 ||
    minute > 59
  )
    return getTranslate('Unavailable')
  const registered = momentJalaali
    .utc(value, 'YYYY-MM-DDTHH:mm', true)
    .locale(lang)
  const formatted = registered.format(
    lang === 'fa' ? 'jYYYY-jMM-jDD HH:mm' : 'YYYY-MM-DD HH:mm'
  )
  // The calendar library has a narrower year range than the generic PMS
  // contract. Preserve the valid Gregorian wall clock rather than show NaN.
  return formatted.includes('NaN')
    ? registered.format('YYYY-MM-DD HH:mm')
    : formatted
}

function NoteStateChip({ state }: { state: string }) {
  const classes = useStyles()
  const normalized = state.toLowerCase()
  const tone =
    normalized === 'delivered'
      ? classes.delivered
      : ['failed', 'blocked'].includes(normalized)
      ? classes.failed
      : [
          'queued',
          'pending',
          'awaiting_mapping',
          'processing',
          'unknown'
        ].includes(normalized)
      ? ''
      : classes.attention
  return (
    <Chip
      size="small"
      className={`${classes.stateChip} ${tone}`}
      label={noteStateLabel(state)}
    />
  )
}

function PmsRegistrationTime({ value }: { value?: string | null }) {
  const formatted = formatPmsRegistration(value)
  const available = formatted !== getTranslate('Unavailable')
  return (
    <span>
      {getTranslate('Registered in PMS')}:{' '}
      {available ? (
        <>
          <bdi dir="ltr">{formatted}</bdi>{' '}
          <span>({getTranslate('PMS local time')})</span>
        </>
      ) : (
        <span>{formatted}</span>
      )}
    </span>
  )
}

const NoteCard = memo(function NoteCard({
  note,
  canViewDeliveryDetails,
  scope
}: {
  note: BookingNote
  canViewDeliveryDetails: boolean
  scope: string
}) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(false)
  return (
    <article className={classes.note}>
      <div className={classes.noteHeader}>
        <Typography className={classes.noteKind}>
          {getTranslate(
            note.kind.toLowerCase() === 'initial'
              ? 'Initial booking note'
              : 'Additional booking note'
          )}
          <span className={classes.noteDate}>
            <bdi dir="ltr">{formatLoginAttemptTime(note.createdAt)}</bdi>
          </span>
        </Typography>
        <div aria-live="polite">
          <NoteStateChip state={note.state} />
        </div>
      </div>
      <div className={classes.noteBody}>
        <Typography className={classes.text} dir="auto">
          {note.text}
        </Typography>
        <div className={classes.targets}>
          <span>{getTranslate('Room stays')}:</span>
          {note.roomNumbers.map((room) => (
            <Chip
              key={room}
              size="small"
              variant="outlined"
              className={classes.targetChip}
              label={<bdi dir="ltr">{room}</bdi>}
            />
          ))}
        </div>
        {canViewDeliveryDetails && note.deliveries.length > 0 && (
          <>
            <Button
              size="small"
              color="primary"
              className={classes.deliveryToggle}
              aria-expanded={expanded}
              aria-controls={`note-delivery-${scope}-${note.id}`}
              onClick={() => setExpanded((value) => !value)}
              endIcon={
                <ExpandMoreIcon
                  className={expanded ? classes.expandedIcon : ''}
                />
              }>
              {getTranslate('PMS delivery details')}
            </Button>
            <div id={`note-delivery-${scope}-${note.id}`} hidden={!expanded}>
              {expanded && (
                <ul className={classes.deliveryList}>
                  {note.deliveries.map((delivery) => (
                    <li
                      key={delivery.deliveryId}
                      className={classes.deliveryRow}>
                      <div className={classes.deliveryMeta}>
                        <span className={classes.deliveryRoom}>
                          {getTranslate('Room stay')}{' '}
                          <bdi dir="ltr">{delivery.roomNumber}</bdi>
                        </span>
                        {(delivery.providerReference ??
                          delivery.traceReference) != null && (
                          <span>
                            {getTranslate('PMS reference')}:{' '}
                            <bdi dir="ltr">
                              {delivery.providerReference ??
                                delivery.traceReference}
                            </bdi>
                          </span>
                        )}
                        <PmsRegistrationTime value={delivery.registeredAt} />
                        {delivery.failureCode && (
                          <span>
                            {getTranslate('Delivery code')}:{' '}
                            <bdi dir="ltr">{delivery.failureCode}</bdi>
                          </span>
                        )}
                      </div>
                      <NoteStateChip state={delivery.state} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </article>
  )
})

function RoomNotesComposer({
  history,
  roomNumber,
  notes
}: {
  history: NotesHistory
  roomNumber: string
  notes: BookingNote[]
}) {
  const classes = useStyles()
  const {
    reservationNumber,
    capability,
    loading,
    historyLoaded,
    hidden,
    mayAdd
  } = history
  const [text, setText] = useState('')
  const [draftId, setDraftId] = useState('')
  const [error, setError] = useState('')
  const [accepted, setAccepted] = useState(false)
  const [addForbidden, setAddForbidden] = useState(false)
  const submitting = useRef(false)
  const failedDraft = useRef(false)
  const [addNote, { loading: saving }] = useMutation<{
    addBookingNote: BookingNote
  }>(ADD_BOOKING_NOTE_MUTATION)
  const roomNumbers = capability?.roomNumbers || []
  const canAdd =
    mayAdd &&
    capability?.canAdd === true &&
    !addForbidden &&
    history.mappedRooms.includes(roomNumber) &&
    roomNumbers.includes(roomNumber)
  const length = Array.from(text).length

  const changeDraft = () => {
    // An uncertain previous attempt must be retried unchanged or deliberately abandoned.
    if (failedDraft.current) {
      setError(getTranslate('Retry the unchanged note or start a new note'))
      return false
    }
    setAccepted(false)
    setError('')
    return true
  }

  const submit = async () => {
    if (
      submitting.current ||
      !canAdd ||
      !text.trim() ||
      length > BOOKING_NOTE_MAX_CHARACTERS
    )
      return
    submitting.current = true
    setError('')
    setAccepted(false)
    try {
      const noteId = draftId || newNoteId()
      setDraftId(noteId)
      const response = await addNote({
        variables: {
          reservationNumber,
          input: { noteId, text, roomNumbers: [roomNumber] }
        }
      })
      if (!response.data?.addBookingNote)
        throw new Error('Missing note response')
      const note = response.data.addBookingNote
      history.append(note)
      setText('')
      setDraftId('')
      failedDraft.current = false
      setAccepted(true)
      // Acceptance is final even if a subsequent history refresh cannot be fetched.
    } catch (failure) {
      const rejection = noteRejection(failure)
      failedDraft.current = rejection === null
      if (rejection) {
        setDraftId('')
        if (rejection === 'permission') setAddForbidden(true)
        setError(
          getTranslate(
            {
              permission:
                'Request rejected: you no longer have permission to add notes. Your draft is retained.',
              conflict:
                'This note identity conflicts with an earlier request. Check history before submitting a new note.',
              invalid:
                'Note rejected. Check the text and this room stay, then refresh eligibility before submitting.'
            }[rejection]
          )
        )
      } else {
        setError(
          getTranslate(
            'Note was not acknowledged. Retry without changing it to avoid duplicates.'
          )
        )
      }
    } finally {
      submitting.current = false
    }
  }

  if (hidden) return null
  return (
    <Paper
      elevation={0}
      className={classes.root}
      component="section"
      dir={getDirection()}
      aria-label={getTranslate('Notes for room stay {number}', {
        number: roomNumber
      })}>
      <div className={classes.header}>
        <div className={classes.titleGroup}>
          <NotesOutlinedIcon
            className={classes.headerIcon}
            aria-hidden="true"
          />
          <div>
            <Typography variant="h4" className={classes.title}>
              {getTranslate('Reservation notes')}
            </Typography>
            <Typography className={classes.subtitle}>
              {getTranslate('Notes and comments are sent to the PMS.')}
            </Typography>
          </div>
        </div>
        <Button
          size="small"
          variant="outlined"
          className={classes.refresh}
          startIcon={<RefreshIcon />}
          disabled={loading}
          onClick={() => {
            history.refresh()
          }}>
          {getTranslate('Refresh all notes')}
        </Button>
      </div>
      <Divider />
      <div className={classes.body}>
        <div role="status" aria-live="polite" className={classes.feedback}>
          {length > BOOKING_NOTE_MAX_CHARACTERS && (
            <Typography>
              {getTranslate('Notes must contain at most {max} characters.', {
                max: BOOKING_NOTE_MAX_CHARACTERS
              })}
            </Typography>
          )}
          {loading && <Typography>{getTranslate('Loading notes')}</Typography>}
          {accepted && (
            <Typography>
              {getTranslate('Note saved. Delivery to the PMS is pending.')}
            </Typography>
          )}
        </div>
        {!!(error || history.error) && (
          <Typography role="alert" className={classes.error}>
            {error || history.error}
          </Typography>
        )}
        {addForbidden && !!text && (
          <TextField
            id={`retained-note-${reservationNumber}-${roomNumber}`}
            label={getTranslate('Retained note draft')}
            value={text}
            multiline
            fullWidth
            rows={3}
            InputProps={{ readOnly: true }}
          />
        )}
        {canAdd && (
          <form
            className={classes.composer}
            aria-label={getTranslate('Add note for room stay {number}', {
              number: roomNumber
            })}
            onSubmit={(event) => {
              event.preventDefault()
              void submit()
            }}>
            <Typography className={classes.sectionTitle}>
              {getTranslate('Add note')}
            </Typography>
            <TextField
              id={`reservation-note-${reservationNumber}-${roomNumber}`}
              label={getTranslate('Additional note')}
              required
              InputLabelProps={{ required: false }}
              inputProps={{ 'aria-required': true }}
              value={text}
              multiline
              fullWidth
              rows={3}
              variant="outlined"
              disabled={saving}
              error={length > BOOKING_NOTE_MAX_CHARACTERS}
              helperText={
                length > BOOKING_NOTE_MAX_CHARACTERS
                  ? getTranslate(
                      'Notes must contain at most {max} characters.',
                      {
                        max: BOOKING_NOTE_MAX_CHARACTERS
                      }
                    )
                  : getTranslate(
                      'Required note text. {count} / {max} characters.',
                      { count: length, max: BOOKING_NOTE_MAX_CHARACTERS }
                    )
              }
              onChange={(event) => {
                if (changeDraft()) setText(event.target.value)
              }}
            />
            {failedDraft.current && !!draftId && (
              <Typography variant="body2" className={classes.retryHint}>
                {getTranslate(
                  'A previous attempt may already have saved this note. Check history before starting a new note.'
                )}
              </Typography>
            )}
            <div className={classes.actions}>
              <Button
                type="submit"
                className={classes.primaryAction}
                variant="contained"
                color="primary"
                disabled={
                  saving || !text.trim() || length > BOOKING_NOTE_MAX_CHARACTERS
                }>
                {getTranslate('Add note')}
              </Button>
              {!!draftId && (
                <Button
                  disabled={saving}
                  onClick={() => {
                    failedDraft.current = false
                    setDraftId('')
                    setText('')
                    setError('')
                    setAccepted(false)
                  }}>
                  {getTranslate('Start a new note')}
                </Button>
              )}
            </div>
          </form>
        )}
        {historyLoaded && (
          <div className={classes.historyHeader}>
            <Typography
              className={classes.sectionTitle}
              style={{ marginBottom: 0 }}>
              {getTranslate('Note history')}
            </Typography>
            <Typography className={classes.historyCount}>
              {getTranslate('{count} notes loaded', { count: notes.length })}
            </Typography>
          </div>
        )}
        <div className={classes.historyList}>
          {notes.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              scope={roomNumber}
              canViewDeliveryDetails={
                capability?.canViewTraceDeliveryDetails === true
              }
            />
          ))}
        </div>
        {!loading &&
          !error &&
          !history.error &&
          capability &&
          !notes.length && (
            <Typography className={classes.empty}>
              {getTranslate(
                capability.hasMore
                  ? 'More note history is available. Load more notes below.'
                  : 'No reservation notes recorded'
              )}
            </Typography>
          )}
      </div>
    </Paper>
  )
}

const MemoRoomNotesComposer = memo(
  RoomNotesComposer,
  (previous, next) =>
    previous.roomNumber === next.roomNumber &&
    previous.notes === next.notes &&
    (Object.keys(previous.history) as (keyof NotesHistory)[]).every(
      (key) =>
        key === 'notesByRoom' || previous.history[key] === next.history[key]
    )
)

export function RoomStayNotes({ roomNumber }: { roomNumber: string }) {
  const history = useContext(HistoryContext)
  if (!history) return null
  return (
    <MemoRoomNotesComposer
      key={`${history.reservationNumber}:${roomNumber}`}
      history={history}
      roomNumber={roomNumber}
      notes={history.notesByRoom.get(roomNumber) || EMPTY_NOTES}
    />
  )
}

function NotesProvider({
  reservationNumber,
  mayAdd,
  roomNumbers,
  children
}: Props & { mayAdd: boolean; roomNumbers: string[]; children: ReactNode }) {
  const classes = useStyles()
  const [page, setPage] = useState(1)
  const [notes, setNotes] = useState<BookingNote[]>([])
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [error, setError] = useState('')
  const [lastCapability, setLastCapability] = useState<BookingNotesPage>()
  const { data, loading, refetch } = useQuery<{
    bookingNotes: BookingNotesPage
  }>(BOOKING_NOTES_QUERY, {
    variables: { reservationNumber, page },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: (failure) => {
      if (forbidden(failure)) setHidden(true)
      else setError(getTranslate('Could not load reservation notes'))
    }
  })
  const capability = data?.bookingNotes || lastCapability
  useEffect(() => {
    if (!data?.bookingNotes) return
    const receivedPage = data.bookingNotes
    setLastCapability(receivedPage)
    setError('')
    setHistoryLoaded(true)
    setNotes((previous) => {
      const merged = new Map(previous.map((note) => [note.id, note]))
      receivedPage.notes.forEach((note) => merged.set(note.id, note))
      return Array.from(merged.values())
    })
  }, [data])
  const projections = useRef(
    new WeakMap<BookingNote, Map<string, BookingNote>>()
  )
  const priorIndex = useRef(new Map<string, BookingNote[]>())
  const notesByRoom = useMemo(() => {
    const index = indexRoomNotes(notes, priorIndex.current, projections.current)
    priorIndex.current = index
    return index
  }, [notes])
  // Historical targets absent from today's room list remain visible read-only;
  // never drop old notes simply because their roomstay can no longer be mapped.
  const unmatched = useMemo(() => {
    const mapped = new Set(roomNumbers)
    return Array.from(notesByRoom.keys()).filter((room) => !mapped.has(room))
  }, [notesByRoom, roomNumbers])
  const refresh = useCallback(() => {
    setError('')
    setPage(1)
    void refetch({ page: 1 }).catch(() => undefined)
  }, [refetch])
  const append = useCallback((note: BookingNote) => {
    setNotes((previous) => [
      note,
      ...previous.filter((item) => item.id !== note.id)
    ])
  }, [])
  const context = useMemo(
    () => ({
      reservationNumber,
      capability,
      notesByRoom,
      loading,
      error,
      historyLoaded,
      hidden,
      mayAdd,
      mappedRooms: roomNumbers,
      refresh,
      append
    }),
    [
      reservationNumber,
      capability,
      notesByRoom,
      loading,
      error,
      historyLoaded,
      hidden,
      mayAdd,
      roomNumbers,
      refresh,
      append
    ]
  )
  return (
    <HistoryContext.Provider value={context}>
      {children}
      {!hidden && unmatched.length > 0 && (
        <section aria-label={getTranslate('Notes for unavailable room stays')}>
          <Typography>
            {getTranslate('Notes for unavailable room stays')}
          </Typography>
          {unmatched.map((room) => (
            <RoomStayNotes key={room} roomNumber={room} />
          ))}
        </section>
      )}
      {!hidden && capability?.hasMore && (
        <div className={classes.actions}>
          <Button
            variant="outlined"
            size="small"
            disabled={loading}
            onClick={() => {
              if (error) {
                setError('')
                void refetch({ page }).catch(() => undefined)
              } else setPage(page + 1)
            }}>
            {getTranslate('Load more notes')}
          </Button>
        </div>
      )}
    </HistoryContext.Provider>
  )
}

export default function ReservationNotes({
  reservationNumber,
  roomNumbers,
  children
}: Props & { roomNumbers: string[]; children: ReactNode }) {
  const features = buildFeatureSet(useReactiveVar(credentialsVar))
  if (!features.has('booking.details.view')) return <>{children}</>
  return (
    <NotesProvider
      key={reservationNumber}
      reservationNumber={reservationNumber}
      mayAdd={features.has('booking.notes.add')}
      roomNumbers={roomNumbers}>
      {children}
    </NotesProvider>
  )
}
